import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Category } from '@ui/src/components/accounts-selector/types';
import {
  DateInputRange,
  AccountsSelector,
  Account,
  Response,
  Table,
  useQueryState,
  useGetList,
  useScroll,
  usePrevious,
  useFilters,
  Filters,
  formatDate,
  SchemaFilterType,
  SortDir,
  PermissionEnum,
  PermissionDenied,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useGetRevaluationAccountsCategoriesQuery,
  useLazyGetRevalutationAccountsListQuery,
  useLazyGetRevalutationListQuery,
} from './store/service';
import {
  schema,
  columns,
  filtersSchema,
  columnsLabels,
} from './variables/list';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { Revaluation as RevaluationProps } from './types';
import useGetAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import { clearAccountingJobs } from '../../store/dictionaries/slice';
import PrecisionSelector from '../../components/partial/reports/precision-selector';

import cn from './revaluation.module.css';

let timeout: NodeJS.Timeout;

const formatRevaluationList = ({
  list,
}: {
  list: RevaluationProps[];
}): RevaluationProps[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let balance = '-';

    if (result?.sumBaseCurrency || result.sum) {
      let balanceParts: string[] = [];
      if (result.sumBaseCurrency) {
        balanceParts = result?.sumBaseCurrency?.toString().split('.');
      } else if (result.sum) {
        balanceParts = result?.sum?.toString().split('.');
      }
      const part = balanceParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const balanceSecondPart = balanceParts[1]
        ? `${balanceParts[1]}${balanceParts[1]?.length === 1 ? '0' : ''}`
        : '00';
      balance = `${part}.${balanceSecondPart}`;
    }

    const render: Record<string, any> = {
      debitAccountId: (
        <div className={cn.revaluationAccountRow}>
          <div className={cn.revaluationAccountDescription}>
            {result.debitAccount.description}
          </div>
          <div className={cn.revaluationAccountFullNumber}>
            {result.debitAccount.fullNumber}
          </div>
        </div>
      ),
      creditAccountId: (
        <div className={cn.revaluationAccountRow}>
          <div className={cn.revaluationAccountDescription}>
            {result.creditAccount.description}
          </div>
          <div className={cn.revaluationAccountFullNumber}>
            {result.creditAccount.fullNumber}
          </div>
        </div>
      ),
      currencyId: <div>{result.currency.name}</div>,
      sumBaseCurrency: <div>{balance}</div>,
      revaluateDate: <div>{formatDate(result.revaluateDate, true)}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const Revaluation = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();
  const [precision, setPrecision] = useState('2');

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const {
    revaluation: { accountCategories, revaluation, pagination, errorsList },
    reportsDictionaries: { currencies, activePermissions, accountingJobs },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { isLoading: accountsCategoriesLoading } =
    useGetRevaluationAccountsCategoriesQuery(
      'all=true&include=primaryAccounts',
    );

  const prevFilters = usePrevious(filters);

  const [
    getAccountsList,
    {
      isLoading: accountsListLoading,
      isFetching: accountsListUpdateInProgress,
    },
  ] = useLazyGetRevalutationAccountsListQuery();

  const [
    getRevaluationList,
    { isLoading: revaluationListLoading, isFetching },
  ] = useLazyGetRevalutationListQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<RevaluationProps>({
      getList: getRevaluationList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { autocompleteList, onSearch: onSearchAccounts } =
    useGetAutocompleteList();

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    onSearchAccounts(searchAutocomplete);
  };

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const getAccountsListAction = useCallback(
    async (query: string, accountId?: string) => {
      if (query && query.length) {
        const response = await getAccountsList(`?${query}&all=true`);

        if (response && response.data) {
          setAccounts(response.data.data.edges.map((item) => item.node));

          if (accountId && !selectedAccount) {
            const currentAccount = response.data.data.edges.find(
              (item) => item.node.id === accountId,
            );

            if (currentAccount) {
              setSelectedAccount(currentAccount.node.fullNumber);
            }
          }
        }
      }
    },
    [],
  );

  const onSearch = async (searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      const response = (await getAccountsList(
        `?search=${searchText}&all=true`,
      )) as unknown as { data: Response<Account> };

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push(item.node);
        });
      }
      setAccounts(results);
    }
  };

  useEffect(() => {
    if (!revaluation && !search && !sort) {
      getListAction({});
    }
  }, [revaluation, search, sort]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      !revaluation &&
      search &&
      filters &&
      prevFilters &&
      sort &&
      Object.keys(sort).length
    ) {
      if (filters.revaluateDate) {
        setDateRange(filters.revaluateDate.value.map((item) => new Date(item)));
      }

      if (filters.roundDecimals) {
        setPrecision(filters.roundDecimals.value[0]);
      }

      if (filters?.accountId?.value?.length) {
        getAccountsListAction(
          `ids=${filters?.accountId?.value[0]}`,
          filters?.accountId?.value[0],
        );
      }

      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, revaluation, sort, search, prevFilters, accounts]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=REVALUATION&type=BALANCES_LOGS&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
      onGetStates(
        '?type=REVALUATION&type=BALANCES_LOGS&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (currencies) {
      setValues('creditAccountId', autocompleteList);
      setValues('currencyId', currencies);
    }
  }, [autocompleteList, currencies]);

  const formattedData = useMemo(() => {
    const data = formatRevaluationList({
      list: revaluation as RevaluationProps[],
    });

    return data;
  }, [revaluation]);

  useEffect(() => {
    let title = 'Revaluation report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (!activePermissions?.includes(PermissionEnum.REPORTS_REVALUATION_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.revaluation} ref={scrollRef}>
        <div className={cn.revaluationTop}>
          <div className={cn.revaluationTitle}>Revaluation report</div>
          <div className={cn.revaluationTopControls}>
            <div className={cn.revaluationTopControlsLeft}>
              <DateInputRange
                placeholder="Select dates"
                className={cn.revaluationDateInput}
                values={dateRange.map((item) => item.toDateString())}
                onChangeRange={(range) => {
                  setDateRange(range);

                  if (range.length) {
                    const nextFilters = { ...filters };

                    nextFilters.revaluateDate = {
                      value: range.map((item) => format(item, 'yyyy-MM-dd')),
                      type: SchemaFilterType.DATE_RANGE,
                    };

                    onFilter({
                      ...nextFilters,
                      sortProp: {
                        value: [
                          sort.sortProp ? sort.sortProp : 'revaluateDate',
                        ],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  } else {
                    const nextFilters = { ...filters };

                    delete nextFilters.revaluateDateFrom;
                    delete nextFilters.revaluateDateTo;
                    delete nextFilters.revaluateDate;

                    onFilter({
                      ...nextFilters,
                      sortProp: {
                        value: [
                          sort.sortProp ? sort.sortProp : 'revaluateDate',
                        ],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  }
                }}
              />
              <AccountsSelector
                accounts={accountCategories as Category[]}
                placeholder="Select account (optional)"
                isLoading={accountsCategoriesLoading}
                selectedAccount={selectedAccount}
                accountsIsLoading={
                  accountsListLoading || accountsListUpdateInProgress
                }
                onSearch={onSearch}
                setAccountQuery={getAccountsListAction}
                accountsList={accounts}
                setAccounts={setAccounts}
                className={cn.accountsSelector}
                setSelectedAccount={(number) => {
                  setSelectedAccount(number);

                  const currentAccount = accounts?.find(
                    (item) => item.fullNumber === number,
                  );

                  if (currentAccount) {
                    onFilter({
                      ...filters,
                      accountId: {
                        value: [currentAccount.id],
                        type: SchemaFilterType.STRING,
                      },
                      sortProp: {
                        value: [
                          sort.sortProp ? sort.sortProp : 'revaluateDate',
                        ],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  } else {
                    const nextFilters = { ...filters };

                    delete nextFilters.accountId;

                    onFilter({
                      ...nextFilters,
                      sortProp: {
                        value: [
                          sort.sortProp ? sort.sortProp : 'revaluateDate',
                        ],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                        type: SchemaFilterType.STRING,
                      },
                    });
                  }
                }}
              />
              <div className={cn.precisionSelector}>
                <PrecisionSelector
                  precision={precision}
                  setPrecision={(value) => {
                    setPrecision(value);

                    const nextFilters = { ...filters };

                    nextFilters.roundDecimals = {
                      value: [value],
                      type: SchemaFilterType.STRING,
                    };

                    onFilterChange({
                      ...nextFilters,
                      sortProp: {
                        value: [
                          sort.sortProp ? sort.sortProp : 'revaluateDate',
                        ],
                        type: SchemaFilterType.STRING,
                      },
                      sortDir: {
                        value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                        type: SchemaFilterType.STRING,
                      },
                    });

                    getListAction({
                      filters: nextFilters,
                      sort: {
                        sortProp: sort.sortProp
                          ? sort.sortProp
                          : 'revaluateDate',
                        sortDir: sort.sortDir ? sort.sortDir : SortDir.ASC,
                      },
                    });
                  }}
                />
              </div>
            </div>
          </div>
          {activePermissions?.includes(
            PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
          ) && (
            <AccountingJob
              accountingJobsIsLoading={
                accountingJobsIsLoading || accountingJobsIsFetching
              }
              accountingJob={currentAccountingJob}
              accountingJobs={accountingJobs}
              jobsLength={accountingJobs?.length}
              query="?type=REVALUATION&&type=BALANCES_LOGS&state=IN_PROGRESS&state=QUEUED&state=NEW"
            />
          )}
        </div>
        <Table
          title=""
          columns={columns}
          data={formattedData || []}
          onSort={(name) => onSort(name)}
          filters={filters}
          sort={sort}
          error={errorsList || errorList}
          filtersComponent={filtersComponent}
          columnsLabels={columnsLabels}
          onFilterChange={(values) => {
            const nextFilters: Filters = {
              ...values,
              sortProp: {
                value: [sort.sortProp ? sort.sortProp : 'revaluateDate'],
                type: SchemaFilterType.STRING,
              },
              sortDir: {
                value: [sort.sortDir ? sort.sortDir : SortDir.ASC],
                type: SchemaFilterType.STRING,
              },
            };

            if (selectedAccount) {
              nextFilters.debitAccountNumber = {
                value: [selectedAccount],
                type: SchemaFilterType.STRING,
              };
            }

            if (dateRange.length) {
              nextFilters.revaluateDate = {
                value: dateRange.map((item) => format(item, 'yyyy-MM-dd')),
                type: SchemaFilterType.DATE_RANGE,
              };
            }

            onFilter({
              ...nextFilters,
            });
          }}
          handleSubmit={handleSubmit}
          isLoading={isFetching || revaluationListLoading}
          take="20"
          setPage={(data) => setPage({ ...data, filters })}
          pagination={pagination}
        />
      </div>
    );
  }

  return content;
};

export default Revaluation;
